import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { init } from '@plausible-analytics/tracker';
import App from './App.tsx';
import './index.css';
import * as Sentry from "@sentry/react";
import { COUNTRY_CONFIG } from './config/countries';
import { LANGUAGE, STRINGS } from './i18n';

// Guard against translation extensions (Google Translate, Edge, DeepL, ...) that
// reparent React-managed DOM nodes, causing "removeChild/insertBefore"
// NotFoundError crashes. Only the notranslate hints in index.html are honored by
// Google; this defends against every extension. See React issue #11538.
if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child;
    return originalRemoveChild.call(this, child) as T;
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

Sentry.init({
  dsn: "https://810a9869fecd01c46af29f7d81872b99@o4510697106046976.ingest.de.sentry.io/4510788399792208",
  // Sentry 11 replaced sendDefaultPii with dataCollection, which defaults to
  // userInfo: true. Keep it off so Sentry does not infer IP addresses.
  dataCollection: { userInfo: false }
});

Sentry.setTag("country", COUNTRY_CONFIG.country);
Sentry.setTag("language", LANGUAGE);

if (typeof window !== 'undefined') {
  init({
    domain: COUNTRY_CONFIG.plausibleDomain,
    endpoint: 'https://plausible.nextpulse.net/api/event',
    outboundLinks: true,
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center px-4 text-center">
          <div>
            <p className="text-gray-900 font-semibold mb-2">
              {STRINGS.errorBoundary.title}
            </p>
            <p className="text-gray-600 text-sm mb-4">
              {STRINGS.errorBoundary.body}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
            >
              {STRINGS.errorBoundary.reload}
            </button>
          </div>
        </div>
      }
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
